import classNames from 'classnames';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {Model as BioJSSequencePrimer} from 'biojs/SequencePrimer';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';

export function isAssemblyPrimer(
  sequence: BioJSSequence,
  primer: BioJSSequencePrimer,
  assemblyType?: ASSEMBLY_TYPE
): boolean {
  // eslint-disable-next-line global-require
  const asm = require('assembly/Asm').default; // circular dependency

  return (
    asm.of(sequence).isFlankingPrimer(sequence, primer) &&
    (assemblyType == null || sequence.assemblyType() === assemblyType)
  );
}

export function basesHTML(
  sequence: BioJSSequence,
  primer: BioJSSequencePrimer,
  options: {isAssemblyPrimer?: boolean}
): string {
  let bindingRegionStart = 0;
  let postRecognitionSiteStart = 0;
  let recognitionSiteStart = 0;
  let stickyEndStart = 0;
  const isGoldenGate = options.isAssemblyPrimer
    ? sequence.assemblyType() === ASSEMBLY_TYPE.GOLDEN_GATE
    : isAssemblyPrimer(sequence, primer, ASSEMBLY_TYPE.GOLDEN_GATE);

  if (isGoldenGate) {
    const enzyme = sequence.assembly()!.enzyme();
    recognitionSiteStart = primer.bases().indexOf(enzyme.get('seq'));
    postRecognitionSiteStart = recognitionSiteStart + enzyme.recognitionSiteLength();
    stickyEndStart = postRecognitionSiteStart + enzyme.postRecognitionSiteLength();
    bindingRegionStart = stickyEndStart + enzyme.stickyEndLength();
  }

  const overhangLength = primer.overhangLength();
  const matchingStatuses = primer.baseMatchingStatuses(sequence);
  return ReactDOMServer.renderToStaticMarkup(
    <>
      {Array.from(primer.bases()).map((base: string, index: number) => {
        const withinOverhang = index < overhangLength;
        const isMatch = matchingStatuses[index];
        const isRecognitionSite =
          isGoldenGate && index >= recognitionSiteStart && index < postRecognitionSiteStart;
        const isStickyEnd = isGoldenGate && index >= stickyEndStart && index < bindingRegionStart;

        return (
          <span
            key={index}
            className={classNames({
              'primer-base-overhang': withinOverhang && !isRecognitionSite && !isStickyEnd,
              'primer-base-recognition-site': withinOverhang && isRecognitionSite,
              'primer-base-sticky-end': withinOverhang && isStickyEnd,
              'primer-base-match': !withinOverhang && isMatch,
              'primer-base-no-match': !withinOverhang && !isMatch,
            })}
          >
            {base}
          </span>
        );
      })}
    </>
  );
}
